/**
 * Wave 38 · ATC Sale Posts (reverse market) + Categories + Media + Countdown Timer.
 * Sellers post items; buyers place offers. Route: mounted under /atc/b2c and /atc/b2b as a tab.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';
import MediaLightbox, { normalizeItems } from '../../components/MediaLightbox';
import CountdownTimer from '../../components/CountdownTimer';
import Sparkline from '../../components/Sparkline';
import ShareBar from '../../components/atc/ShareBar';

const CONDITIONS = [
  { key: 'new',       label: '✨ New (Sealed)' },
  { key: 'like_new',  label: '🌟 Like New (barely used)' },
  { key: 'used',      label: '👍 Used (good condition)' },
  { key: 'old',       label: '📦 Old (working, cosmetic wear)' },
];

// ================= Categories Dropdown (searchable + add-new) =================
export function CategoryPicker({ value, onChange, testid = 'atc-cat-picker' }) {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get('/atc/categories'); setCats(r.data.categories || []); }
    catch (_) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const filtered = cats.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const addNew = async () => {
    const name = q.trim();
    if (name.length < 2) return;
    setAdding(true);
    try {
      const r = await api.post('/atc/categories', { name });
      const c = r.data.category;
      setCats((prev) => (prev.find((x) => x.slug === c.slug) ? prev : [c, ...prev]));
      onChange(c.name); setOpen(false); setQ('');
      toast.success(r.data.existed ? '↩ Existing category' : '✅ New category added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
    finally { setAdding(false); }
  };
  return (
    <div className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-btn`}
              className="w-full text-left px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm">
        {value || <span className="opacity-50">Category chuno / add karo</span>}
        <span className="float-right opacity-60">▾</span>
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full rounded-lg bg-slate-950 border border-cyan-400/40 shadow-2xl max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-slate-800">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search / naya add karo…"
                   data-testid={`${testid}-search`}
                   className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map((c) => (
              <button key={c.id} type="button" onClick={() => { onChange(c.name); setOpen(false); setQ(''); }}
                      data-testid={`${testid}-opt-${c.slug}`}
                      className="w-full text-left px-3 py-1.5 hover:bg-cyan-500/20 text-xs flex justify-between">
                <span>{c.name}</span>
                <span className="opacity-50 text-[10px]">used {c.use_count}×</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <button type="button" onClick={addNew} disabled={adding || q.trim().length < 2}
                      data-testid={`${testid}-add`}
                      className="w-full text-left px-3 py-2 hover:bg-emerald-500/20 text-xs font-black text-emerald-300 disabled:opacity-50">
                ➕ Add "{q.trim()}" as new category
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ================= Media Uploader (images + video + live cam) =================
export function MediaUploader({ images, video, onImages, onVideo }) {
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const upload = async (file, kind) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('kind', kind);
      const r = await api.post('/atc/sale-post/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data.url;
    } finally { setBusy(false); }
  };
  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - images.length);
    for (const f of files) {
      try { const url = await upload(f, 'image'); onImages([...images, url].slice(0, 5)); }
      catch (err) { toast.error('Image upload failed'); }
    }
    e.target.value = '';
  };
  const onPickVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!window.confirm(`Video upload par 10 AT anti-spam hold lagega (deal lock hone par refund). Proceed?`)) { e.target.value = ''; return; }
    try { const url = await upload(f, 'video'); onVideo(url); toast.success('✅ Video uploaded (10 AT held)'); }
    catch (err) { toast.error('Video upload failed'); }
    e.target.value = '';
  };
  return (
    <div className="space-y-2" data-testid="atc-media-uploader">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="cursor-pointer px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs font-black hover:bg-slate-700"
               data-testid="atc-media-img-btn">
          📸 Photos ({images.length}/5)
          <input type="file" accept="image/*" multiple onChange={onPickImages} className="hidden" data-testid="atc-media-img-input" />
        </label>
        <label className="cursor-pointer px-3 py-1.5 rounded-lg bg-slate-800 border border-amber-400/40 text-xs font-black hover:bg-slate-700"
               data-testid="atc-media-vid-btn">
          🎥 Video (10 AT) {video ? '✓' : ''}
          <input type="file" accept="video/*" onChange={onPickVideo} className="hidden" data-testid="atc-media-vid-input" />
        </label>
        <button type="button" onClick={() => setCamOpen(true)} data-testid="atc-media-cam-btn"
                className="px-3 py-1.5 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/50 text-xs font-black hover:bg-fuchsia-500/30">
          📷 Live Camera
        </button>
        {busy && <span className="text-[10px] opacity-70">⏳ Uploading…</span>}
      </div>
      {images.length > 0 && (
        <div className="flex gap-1 flex-wrap" data-testid="atc-media-img-preview">
          {images.map((u, i) => (
            <div key={i} className="relative w-16 h-16 rounded overflow-hidden border border-slate-700">
              <img src={u} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => onImages(images.filter((_, k) => k !== i))}
                      className="absolute top-0 right-0 bg-rose-500/90 text-white text-[10px] px-1 rounded-bl">×</button>
            </div>
          ))}
        </div>
      )}
      {video && (
        <div className="relative w-fit" data-testid="atc-media-vid-preview">
          <video src={video} className="w-40 h-24 rounded object-cover border border-amber-400/50" controls muted />
          <button type="button" onClick={() => onVideo(null)}
                  className="absolute top-1 right-1 bg-rose-500/90 text-white text-[10px] px-1.5 rounded">×</button>
        </div>
      )}
      {camOpen && <LiveCameraModal onCapture={async (blob) => {
        const f = new File([blob], `cam-${Date.now()}.jpg`, { type: 'image/jpeg' });
        try { const url = await upload(f, 'image'); onImages([...images, url].slice(0, 5)); toast.success('📷 Snap uploaded'); }
        catch (_) { toast.error('Snap failed'); }
        setCamOpen(false);
      }} onClose={() => setCamOpen(false)} />}
    </div>
  );
}


function LiveCameraModal({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (cancel) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
      } catch (e) { toast.error('Camera access denied'); onClose(); }
    })();
    return () => { cancel = true; if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [onClose]);
  const snap = () => {
    const v = videoRef.current; if (!v) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob((b) => { if (b) onCapture(b); }, 'image/jpeg', 0.85);
  };
  return (
    <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4" data-testid="atc-cam-modal">
      <div className="max-w-md w-full rounded-2xl bg-slate-950 border-2 border-fuchsia-400/50 p-3">
        <div className="text-sm font-black mb-2">📷 Live Camera — Snap Item Photo</div>
        <video ref={videoRef} playsInline muted className="w-full rounded-lg bg-black mb-2" />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} data-testid="atc-cam-close" className="px-3 py-1.5 rounded-lg bg-slate-700 text-xs font-black">Cancel</button>
          <button onClick={snap} data-testid="atc-cam-snap"
                  className="px-4 py-1.5 rounded-lg bg-fuchsia-500 text-slate-900 text-xs font-black hover:brightness-110">📸 Snap</button>
        </div>
      </div>
    </div>
  );
}


// ================= Create Sale Post Modal =================
export function CreateSalePostModal({ dealType, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '', description: '', category: '', condition: 'used',
    price_expectation_at: '', quantity: 1, city: '', expiry_hours: 24,
  });
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const runAutoTag = async () => {
    if (images.length === 0) { toast.info('Pehle 1 photo upload karo, phir AI suggest karega'); return; }
    setAiBusy(true);
    try {
      const r = await api.post('/atc/sale-post/auto-tag', { image_url: images[0] });
      const d = r.data;
      setForm((f) => ({
        ...f,
        title: f.title || d.title,
        description: f.description || d.description,
        category: f.category || d.category,
        condition: d.condition || f.condition,
        price_expectation_at: f.price_expectation_at || d.price_range_at?.high || 500,
      }));
      toast.success(d.used_ai ? '🤖 AI ne suggestions bhar diye' : '📝 Fallback suggestion loaded');
    } catch (_) { toast.error('AI auto-tag failed'); }
    finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!form.title.trim() || !form.category.trim() || !form.price_expectation_at) {
      toast.error('Title, category, price required'); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        price_expectation_at: Number(form.price_expectation_at),
        quantity: Number(form.quantity || 1),
        expiry_hours: Number(form.expiry_hours || 24),
        deal_type: dealType,
        images, video_url: video,
      };
      const r = await api.post('/atc/sale-post', payload);
      toast.success('🏷️ Sale post live!');
      onCreated?.(r.data.post); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Post failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="atc-sale-post-modal" onClick={onClose}>
      <div className="max-w-lg w-full rounded-2xl bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 border-2 border-emerald-400/50 p-4 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-lg font-black text-emerald-200">🏷️ Post Item for Sale</div>
            <div className="text-[11px] opacity-75">Purani cheez, extra stock, second-hand — sab bech do!</div>
          </div>
          <button onClick={onClose} data-testid="atc-sale-post-close" className="text-slate-300 text-xl px-1">×</button>
        </div>

        <div className="space-y-2">
          <MediaUploader images={images} video={video} onImages={setImages} onVideo={setVideo} />
          <button type="button" onClick={runAutoTag} disabled={aiBusy || images.length === 0}
                  data-testid="atc-sale-ai-tag"
                  className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white text-xs font-black disabled:opacity-50">
            {aiBusy ? '🤖 AI thinking…' : '🤖 AI Auto-Fill from Photo'}
          </button>

          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                 placeholder="Item title (e.g. Purani cycle 5 saal use)"
                 data-testid="atc-sale-title"
                 className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm" />

          <div className="grid grid-cols-2 gap-2">
            <CategoryPicker value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="atc-sale-cat" />
            <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
                    data-testid="atc-sale-condition"
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm">
              {CONDITIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>

          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Description — kya kya theek hai, kya scratch hai, dimensions, age…"
                    data-testid="atc-sale-desc" rows={3}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm" />

          <div className="grid grid-cols-3 gap-2">
            <input type="number" value={form.price_expectation_at} onChange={(e) => setForm({ ...form, price_expectation_at: e.target.value })}
                   placeholder="Price (AT)" data-testid="atc-sale-price"
                   className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm" />
            <input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                   placeholder="Qty" data-testid="atc-sale-qty" min="1"
                   className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm" />
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                   placeholder="City" data-testid="atc-sale-city"
                   className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm" />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-[11px] opacity-75">Expires in:</label>
            {[6, 12, 24, 72].map((h) => (
              <button key={h} type="button" onClick={() => setForm({ ...form, expiry_hours: h })}
                      data-testid={`atc-sale-expiry-${h}`}
                      className={`px-2 py-1 rounded text-[10px] font-black ${form.expiry_hours === h ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
                {h}h
              </button>
            ))}
          </div>

          <button onClick={submit} disabled={busy} data-testid="atc-sale-submit"
                  className="w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 text-sm font-black hover:brightness-110 disabled:opacity-50">
            {busy ? '⏳ Posting…' : '🚀 Publish Sale Post'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ================= Sale Posts Grid (with per-post card) =================
export default function SalePostsGrid({ dealType, city, category, currentUser }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [activePost, setActivePost] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);      // Wave 40
  const [wishlist, setWishlist] = useState(new Set());          // Wave 40
  const [wishDropCount, setWishDropCount] = useState(0);        // Wave 40
  const [follows, setFollows] = useState(new Set());            // Wave 40
  const [activeCategory, setActiveCategory] = useState(null);   // Wave 42 · filter from cloud
  const [viewMode, setViewMode] = useState('browse');           // Wave 43 · 'browse' | 'my-buys' | 'my-sells'
  const [myDealsStats, setMyDealsStats] = useState({ locked: 0, completed: 0 });

  const effectiveCategory = activeCategory || category;

  // Support fix 2026-09-24: shared link /atc/b2c?sale=<id> opens that post directly
  useEffect(() => {
    try {
      const sid = new URLSearchParams(window.location.search).get('sale');
      if (sid) api.get(`/atc/sale-post/${sid}`).then((r) => r.data && setActivePost(r.data)).catch(() => {});
    } catch (_) {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === 'my-buys' || viewMode === 'my-sells') {
        const r = await api.get('/atc/sale-post/my-deals', {
          params: { as_role: viewMode === 'my-buys' ? 'buyer' : 'seller', limit: 40 },
        });
        setPosts(r.data.posts || []);
        setMyDealsStats({
          locked: r.data.locked_count || 0,
          completed: r.data.completed_count || 0,
          urgent: r.data.urgent_count || 0,
        });
      } else {
        const r = await api.get('/atc/sale-posts', { params: { deal_type: dealType, city, category: effectiveCategory, limit: 40 } });
        setPosts(r.data.posts || []);
      }
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, [dealType, city, effectiveCategory, viewMode]);
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);

  // Wave 43 · light "my locked deals" count for badge (even in browse mode)
  useEffect(() => {
    if (!currentUser?.id) return;
    api.get('/atc/sale-post/my-deals', { params: { as_role: 'buyer', status: 'LOCKED', limit: 1 } })
      .then((r) => setMyDealsStats((s) => ({ ...s, locked: r.data.locked_count || 0 })))
      .catch(() => {});
  }, [currentUser?.id, posts.length]);

  // Wave 40 · load wishlist + follows once for current user
  const loadWishlist = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const r = await api.get('/atc/wishlist');
      const ids = new Set((r.data.items || []).map((i) => i.post.id));
      setWishlist(ids);
      setWishDropCount(r.data.drop_count || 0);
    } catch (_) { /* silent */ }
  }, [currentUser?.id]);
  const loadFollows = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const r = await api.get('/atc/category-follows');
      setFollows(new Set((r.data.follows || []).map((f) => f.category)));
    } catch (_) { /* silent */ }
  }, [currentUser?.id]);
  useEffect(() => { loadWishlist(); loadFollows(); }, [loadWishlist, loadFollows]);

  const toggleWishlist = async (postId) => {
    if (!currentUser?.id) { toast.error('Login required'); return; }
    const has = wishlist.has(postId);
    try {
      if (has) { await api.delete(`/atc/wishlist/${postId}`); }
      else { await api.post(`/atc/wishlist/${postId}`); }
      setWishlist((prev) => {
        const s = new Set(prev);
        if (has) s.delete(postId); else s.add(postId);
        return s;
      });
      toast.success(has ? '💔 Removed from wishlist' : '💖 Saved to wishlist');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggleFollow = async (cat) => {
    if (!currentUser?.id) { toast.error('Login required'); return; }
    const has = follows.has(cat);
    try {
      if (has) { await api.delete(`/atc/category-follow/${encodeURIComponent(cat)}`); }
      else { await api.post('/atc/category-follow', { category: cat }); }
      setFollows((prev) => {
        const s = new Set(prev);
        if (has) s.delete(cat); else s.add(cat);
        return s;
      });
      toast.success(has ? `🔕 Unfollowed ${cat}` : `🔔 Following ${cat} — deal alerts on WhatsApp`);
    } catch (e) { toast.error('Failed'); }
  };

  const openPoster = () => {
    if (!currentUser?.id) { toast.error('Login required'); return; }
    const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    window.open(`${backend}/api/atc/seller/${currentUser.id}/sale-poster.html`, '_blank');
  };

  return (
    <section className="max-w-7xl mx-auto relative z-10 mt-4" data-testid="atc-sale-posts-section">
      {/* Wave 41 · Launch Party strip (if live) */}
      {viewMode === 'browse' && <LaunchPartyStrip />}

      {/* Wave 41 · Trending Category Cloud */}
      {viewMode === 'browse' && <TrendingCategoryCloud onPick={(cat) => { setActiveCategory(cat); toast.success(`🏷️ Filtering: ${cat}`); }} />}

      {/* Wave 42 · Active category filter pill */}
      {activeCategory && viewMode === 'browse' && (
        <div className="mb-3 flex items-center gap-2" data-testid="atc-active-filter">
          <span className="px-3 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-400/50 text-cyan-200 text-xs font-black flex items-center gap-2">
            🏷️ Filtered by: <b>{activeCategory}</b>
            <button onClick={() => setActiveCategory(null)} data-testid="atc-active-filter-clear"
                    className="ml-1 opacity-80 hover:opacity-100 text-sm leading-none">×</button>
          </span>
          <span className="text-[10px] opacity-60">{posts.length} post{posts.length !== 1 ? 's' : ''} in this category</span>
        </div>
      )}

      {/* Wave 40 · Weekly Sellers Leaderboard */}
      {viewMode === 'browse' && <SellersLeaderboardBanner />}

      {/* Wave 39 · Deal of the Hour banner */}
      {viewMode === 'browse' && <DealOfTheHourBanner dealType={dealType} onOpen={setActivePost} />}

      {/* Wave 43 · Mode toggle (Browse · My Buys · My Sells) */}
      {currentUser?.id && (
        <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="atc-view-mode-toggle">
          <button onClick={() => setViewMode('browse')} data-testid="atc-mode-browse"
                  className={`px-3 py-1.5 rounded-lg text-xs font-black ${viewMode === 'browse' ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
            🛍️ Browse Bazaar
          </button>
          <button onClick={() => setViewMode('my-buys')} data-testid="atc-mode-my-buys"
                  className={`relative px-3 py-1.5 rounded-lg text-xs font-black ${viewMode === 'my-buys' ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
            🔒 My Locked Deals
            {myDealsStats.locked > 0 && (
              <span data-testid="atc-mode-my-buys-badge"
                    className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-black flex items-center justify-center animate-pulse">
                {myDealsStats.locked}
              </span>
            )}
          </button>
          <button onClick={() => setViewMode('my-sells')} data-testid="atc-mode-my-sells"
                  className={`px-3 py-1.5 rounded-lg text-xs font-black ${viewMode === 'my-sells' ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
            🏷️ My Sales
          </button>
          {(viewMode === 'my-buys' || viewMode === 'my-sells') && (
            <span className="text-[10px] opacity-70 ml-1">
              🔒 {myDealsStats.locked} locked · ✅ {myDealsStats.completed} completed
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="text-lg font-black text-emerald-300">
            {viewMode === 'my-buys' ? '🔒 My Locked & Completed Buys' :
             viewMode === 'my-sells' ? '🏷️ My Sales · Locked & Completed' :
             `🏷️ Sellers Bazaar · ${dealType === 'B2B_BULK' ? 'B2B Bulk' : 'B2C Retail'}`}
          </div>
          <div className="text-[11px] opacity-70">
            {viewMode === 'my-buys' ? 'Har locked deal me OTP daalke complete karo · escrow-safe payout' :
             viewMode === 'my-sells' ? 'Har locked sale me buyer ko OTP share karo aur payment paao' :
             'Purani cheez, second-hand, extra stock — sab yahaan bechna hai'}
          </div>
        </div>
        <div className="flex gap-1 flex-wrap items-center">
          {currentUser?.id && (
            <>
              <AlertsBell />
              <button onClick={() => setShowWishlist(true)} data-testid="atc-wishlist-btn"
                      className="relative px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-rose-400/40 text-rose-200 text-xs font-black">
                💖 Wishlist ({wishlist.size})
                {wishDropCount > 0 && (
                  <span data-testid="atc-wishlist-drop-badge"
                        className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-black animate-pulse">
                    {wishDropCount}💸
                  </span>
                )}
              </button>
              <button onClick={openPoster} data-testid="atc-sale-poster-btn"
                      className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-cyan-400/40 text-cyan-200 text-xs font-black">
                🖨 Print My Poster
              </button>
              <button onClick={() => setShowAnalytics((v) => !v)} data-testid="atc-sale-analytics-toggle"
                      className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-fuchsia-400/40 text-fuchsia-200 text-xs font-black">
                📊 {showAnalytics ? 'Hide' : 'My Sales Stats'}
              </button>
            </>
          )}
          <button onClick={() => setShowCreate(true)} data-testid="atc-sale-post-cta"
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 text-sm font-black hover:brightness-110">
            ➕ Sell Something
          </button>
        </div>
      </div>

      {showAnalytics && currentUser?.id && <SaleAnalyticsBlock userId={currentUser.id} />}

      {/* Wave 44 · Deal Timer Nudge banner (only visible on my-buys with urgent items) */}
      {viewMode === 'my-buys' && myDealsStats.urgent > 0 && (
        <div className="mb-3 rounded-xl p-3 bg-gradient-to-r from-rose-900 via-rose-800 to-slate-900 border-2 border-rose-400/60 shadow-lg shadow-rose-500/30 animate-pulse"
             data-testid="atc-timer-nudge-banner">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-2xl">⏰</span>
            <div className="flex-1">
              <div className="text-sm font-black text-rose-100">
                {myDealsStats.urgent} deal{myDealsStats.urgent !== 1 ? 's' : ''} · OTP jaldi expire hone wala hai!
              </div>
              <div className="text-[11px] opacity-90">
                Seller ko yaad dilaao (WhatsApp nudge) warna deal cancel ho jayegi aur paisa refund lag jayega.
              </div>
            </div>
            <span className="px-2 py-1 rounded-lg bg-rose-500 text-white text-xs font-black">{myDealsStats.urgent}</span>
          </div>
        </div>
      )}

      {loading && posts.length === 0 ? (
        <div className="text-center py-6 text-xs opacity-60">⏳ Loading sale posts…</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-8 text-xs opacity-60 rounded-xl border border-dashed border-slate-700">
          {viewMode === 'my-buys' ? 'Abhi koi locked buy nahi hai. Kisi sale-post pe offer thoko aur deal lock karo! 🔒' :
           viewMode === 'my-sells' ? 'Abhi koi locked sale nahi hai. Kuch bech ke pehli deal lock karo! 🏷️' :
           'Abhi koi sale post nahi hai. First seller bano → community shocked ho jayegi! 🚀'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {posts.map((p) => (
            <SalePostCard key={p.id} post={p} currentUser={currentUser}
                          onOpen={() => setActivePost(p)} onChanged={load}
                          isWishlisted={wishlist.has(p.id)} onWishlistToggle={() => toggleWishlist(p.id)}
                          isFollowed={follows.has(p.category)} onFollowToggle={() => toggleFollow(p.category)} />
          ))}
        </div>
      )}

      {showCreate && <CreateSalePostModal dealType={dealType} onClose={() => setShowCreate(false)} onCreated={load} />}
      {activePost && <SalePostDetailModal post={activePost} currentUser={currentUser} onClose={() => setActivePost(null)} onChanged={() => { load(); setActivePost(null); }} />}
      {showWishlist && <WishlistDrawer onClose={() => { setShowWishlist(false); loadWishlist(); }} onOpen={(p) => { setActivePost(p); setShowWishlist(false); }} />}
    </section>
  );
}


// ================= Sale Post Card =================
function SalePostCard({ post, currentUser, onOpen, onChanged, isWishlisted, onWishlistToggle, isFollowed, onFollowToggle }) {
  const [badge, setBadge] = useState(post.seller_trust ? {
    verified: (post.seller_trust.completed_sales || 0) >= 5,
    ...post.seller_trust,
  } : null);
  useEffect(() => {
    api.get(`/atc/seller/${post.seller_id}/badge`).then((r) => setBadge(r.data)).catch(() => {});
  }, [post.seller_id]);
  const isMine = currentUser?.id === post.seller_id;
  const boosted = post._boosted;
  const relist = async (e) => {
    e.stopPropagation();
    try { await api.post(`/atc/sale-post/${post.id}/relist`); toast.success('🔄 Relisted'); onChanged?.(); }
    catch (err) { toast.error('Relist failed'); }
  };
  const boost = async (e) => {
    e.stopPropagation();
    if (!window.confirm('15 AT charge lagega for 6h top-pin. Proceed?')) return;
    try { await api.post(`/atc/sale-post/${post.id}/boost`); toast.success('🚀 Boosted! Top pin for 6h'); onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Boost failed'); }
  };

  return (
    <div onClick={onOpen} data-testid={`atc-sale-card-${post.id.slice(0,8)}`}
         className={`group relative rounded-xl p-3 cursor-pointer transition hover:scale-[1.02] ${
           boosted ? 'bg-gradient-to-br from-fuchsia-950 via-slate-900 to-slate-900 border-2 border-fuchsia-400/60 shadow-lg shadow-fuchsia-500/20' :
           'bg-slate-900 border border-slate-700 hover:border-emerald-400/50'
         }`}>
      {boosted && <div className="text-[9px] font-black text-fuchsia-300 mb-1 animate-pulse">⚡ BOOSTED · TOP PIN</div>}
      {post.images?.length > 0 && (
        <div className="relative">
          <img src={post.images[0]} alt="" className="w-full h-32 object-cover rounded-lg mb-2" />
          {!isMine && currentUser?.id && (
            <button onClick={(e) => { e.stopPropagation(); onWishlistToggle?.(); }}
                    data-testid={`atc-sale-heart-${post.id.slice(0,8)}`}
                    className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-slate-950/70 backdrop-blur border border-rose-400/40 flex items-center justify-center text-lg hover:scale-110 transition">
              {isWishlisted ? '💖' : '🤍'}
            </button>
          )}
        </div>
      )}
      {!post.images?.length && !isMine && currentUser?.id && (
        <button onClick={(e) => { e.stopPropagation(); onWishlistToggle?.(); }}
                data-testid={`atc-sale-heart-${post.id.slice(0,8)}`}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-slate-950/70 border border-rose-400/40 flex items-center justify-center text-lg hover:scale-110 transition z-10">
          {isWishlisted ? '💖' : '🤍'}
        </button>
      )}
      <div className="flex items-start justify-between gap-1 mb-1">
        <div className="text-sm font-black line-clamp-2 flex-1">{post.title}</div>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 whitespace-nowrap font-black">
          {post.condition === 'new' ? '✨ NEW' : post.condition === 'like_new' ? '🌟 LIKE NEW' : post.condition === 'used' ? '👍 USED' : '📦 OLD'}
        </span>
      </div>
      {/* Wave 42-fix · Status pill for locked/completed */}
      {post.status !== 'OPEN' && (
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black mb-1 ${
          post.status === 'LOCKED' ? 'bg-amber-500/25 text-amber-200 border border-amber-400/40' :
          post.status === 'COMPLETED' ? 'bg-emerald-500/25 text-emerald-200 border border-emerald-400/40' :
          post.status === 'EXPIRED' ? 'bg-slate-500/25 text-slate-300 border border-slate-500/40' :
          'bg-rose-500/25 text-rose-200 border border-rose-400/40'
        }`} data-testid={`atc-sale-status-${post.id.slice(0,8)}`}>
          {post.status === 'LOCKED' ? '🔒 LOCKED — awaiting OTP' :
           post.status === 'COMPLETED' ? '✅ COMPLETED' :
           post.status === 'EXPIRED' ? '⏱ EXPIRED' : post.status}
        </div>
      )}
      {/* Wave 44 · Per-card SLA chip + Nudge seller button */}
      {post.status === 'LOCKED' && post.sla_status && post.sla_status !== 'safe' && (
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black mb-1 ml-1 ${
          post.sla_status === 'expired' ? 'bg-rose-600 text-white animate-pulse' :
          post.sla_status === 'critical' ? 'bg-rose-500/40 text-rose-200 border border-rose-400/60 animate-pulse' :
          'bg-amber-500/30 text-amber-200 border border-amber-400/50'
        }`} data-testid={`atc-sla-chip-${post.id.slice(0,8)}`}>
          ⏰ OTP {post.sla_status === 'expired' ? 'EXPIRED' : `in ${post.hours_to_otp_expiry}h`}
        </div>
      )}
      {post.status === 'LOCKED' && !isMine && currentUser?.id && (post.sla_status === 'warning' || post.sla_status === 'critical') && (
        <button onClick={(e) => {
          e.stopPropagation();
          const text = `Bhai, ATC deal ka OTP share karo — deal expire hone wala hai. Item: ${post.title} · ₹${post.locked_price_at} AT.`;
          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
        }} data-testid={`atc-nudge-${post.id.slice(0,8)}`}
                className="w-full mt-1 px-2 py-1 rounded bg-cyan-500/30 text-cyan-200 text-[10px] font-black hover:bg-cyan-500/50">
          🔔 Nudge Seller for OTP
        </button>
      )}
      <div className="text-[10px] opacity-70 mb-1 flex items-center gap-2 flex-wrap">
        <span>🏷️ {post.category}</span>
        {!isMine && currentUser?.id && onFollowToggle && (
          <button onClick={(e) => { e.stopPropagation(); onFollowToggle(); }}
                  data-testid={`atc-sale-follow-${post.id.slice(0,8)}`}
                  title={`${isFollowed ? 'Unfollow' : 'Follow'} ${post.category} for deal alerts`}
                  className={`px-1 py-0 rounded text-[9px] font-black ${isFollowed ? 'bg-cyan-500/30 text-cyan-200' : 'bg-slate-800 text-slate-400 hover:text-cyan-300'}`}>
            {isFollowed ? '🔔' : '🔕'}
          </button>
        )}
        {post.city && <span>📍 {post.city}</span>}
        <span>📦 Qty {post.quantity}</span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-2">
        <div className="text-lg font-black text-emerald-300">₹{post.price_expectation_at} <span className="text-[10px] opacity-70">AT</span></div>
        <CountdownTimer expiresAt={post.expires_at} compact />
      </div>
      <div className="text-[10px] opacity-70 mt-1 flex items-center gap-1 flex-wrap">
        <span>@{post.seller_username || 'seller'}</span>
        {badge?.verified && <span data-testid={`atc-verified-${post.seller_id.slice(0,8)}`}
                                  className="px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[9px] font-black">✓ Verified</span>}
        {badge && badge.rating_count > 0 && (
          <span data-testid={`atc-rating-chip-${post.id.slice(0,8)}`}
                title={`${badge.avg_rating}★ from ${badge.rating_count} buyers`}
                className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-black">
            ⭐{badge.avg_rating}
          </span>
        )}
        {badge && badge.trust_score > 0 && (
          <span data-testid={`atc-trust-chip-${post.id.slice(0,8)}`}
                title={`Trust = avg⭐ × completed − disputes×2 = ${badge.trust_score}`}
                className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                  badge.trust_score >= 20 ? 'bg-fuchsia-500/30 text-fuchsia-200 border border-fuchsia-400/40' :
                  badge.trust_score >= 5  ? 'bg-cyan-500/20 text-cyan-300' :
                                            'bg-slate-700/40 text-slate-300'
                }`}>
            🏅{badge.trust_score}
          </span>
        )}
        <span className="ml-auto">💬 {post.offer_count || 0} offers</span>
      </div>
      {isMine && post.status === 'OPEN' && (
        <div className="mt-2 flex gap-1">
          <button onClick={boost} data-testid={`atc-sale-boost-${post.id.slice(0,8)}`}
                  className="flex-1 px-2 py-1 rounded bg-fuchsia-500/30 text-fuchsia-200 text-[10px] font-black hover:bg-fuchsia-500/50">
            🚀 Boost (15 AT · 6h)
          </button>
        </div>
      )}
      {isMine && (post.status === 'EXPIRED' || post.status === 'CLOSED') && (
        <button onClick={relist} data-testid={`atc-sale-relist-${post.id.slice(0,8)}`}
                className="w-full mt-2 px-2 py-1 rounded bg-cyan-500/20 text-cyan-200 text-[10px] font-black">
          🔄 Re-list this post
        </button>
      )}
      {/* Support fix 2026-09-24: Sellers Bazaar items can be shared (WhatsApp/FB/Instagram/link/image) */}
      {post.status === 'OPEN' && (() => {
        const origin = window.location.origin;
        const refQs = currentUser?.username ? `?ref=${encodeURIComponent(currentUser.username)}` : '';
        const text = `🏷️ For sale on Anant Sellers Bazaar:\n\n"${post.title}"\n💰 ${post.price_expectation_at} AT · 📦 Qty ${post.quantity}${post.city ? ` · 📍 ${post.city}` : ''}\n\nMake an offer:`;
        return (
          <div className="mt-2 border-t border-slate-700/60 pt-1">
            <ShareBar shareUrl={`${origin}/api/atc/sale-post/${post.id}/share${refQs}`} text={text} title={post.title}
                      imageUrl={`${origin}/api/atc/sale-post/${post.id}/og.png`} testPrefix={`atc-sale-share-${post.id.slice(0,8)}`} />
          </div>
        );
      })()}
    </div>
  );
}


// ================= Sale Post Detail Modal (offers + counter + accept) =================
function SalePostDetailModal({ post, currentUser, onClose, onChanged }) {
  const [offers, setOffers] = useState([]);
  const [sellerView, setSellerView] = useState(false);
  const [busy, setBusy] = useState(false);
  const [offerPrice, setOfferPrice] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const [freshPost, setFreshPost] = useState(post);       // Wave 42-fix · live post state
  const [otp, setOtp] = useState(null);                    // seller-side generated OTP
  const [buyerOtp, setBuyerOtp] = useState('');            // buyer OTP input
  const [otpBusy, setOtpBusy] = useState(false);
  const isMine = currentUser?.id === freshPost.seller_id;
  const isLockedBuyer = currentUser?.id === freshPost.buyer_id && freshPost.status === 'LOCKED';

  const load = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([
        api.get(`/atc/sale-post/${post.id}/offers`),
        api.get(`/atc/sale-post/${post.id}`),
      ]);
      setOffers(o.data.offers || []); setSellerView(o.data.seller_view || false);
      setFreshPost(p.data);
    } catch (_) { /* silent */ }
  }, [post.id]);
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [load]);

  const placeOffer = async () => {
    const v = Number(offerPrice);
    if (!v || v <= 0) { toast.error('Valid price daalo'); return; }
    setBusy(true);
    try {
      await api.post(`/atc/sale-post/${post.id}/offer`, { price_at: v });
      toast.success('💬 Offer bhej diya');
      setOfferPrice(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Offer failed'); }
    finally { setBusy(false); }
  };
  const accept = async (offer) => {
    const price = offer.counter_price_at || offer.price_at;
    if (!window.confirm(`Accept @${offer.buyer_username}'s offer @ ₹${price} AT? Escrow lock ho jayega.`)) return;
    try {
      await api.post(`/atc/sale-post/${post.id}/accept-offer`, { offer_id: offer.id });
      toast.success('🔒 Deal locked! Ab OTP generate karo aur buyer ko share karo.'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Accept failed'); }
  };
  const counter = async (offer) => {
    const val = window.prompt(`Counter price @ ₹? (original: ₹${offer.price_at} AT)`);
    const v = Number(val);
    if (!v || v <= 0) return;
    try {
      await api.post(`/atc/sale-post/${post.id}/counter-offer`, { offer_id: offer.id, counter_price_at: v });
      toast.success('↔ Counter sent'); load();
    } catch (e) { toast.error('Counter failed'); }
  };

  // Wave 42-fix · OTP flow
  const generateOtp = async () => {
    setOtpBusy(true);
    try {
      const r = await api.post(`/atc/sale-post/${post.id}/generate-otp`);
      setOtp(r.data.otp);
      toast.success(r.data.reused ? '✅ OTP loaded (still valid)' : '✅ OTP generated · Share with buyer');
    } catch (e) { toast.error(e?.response?.data?.detail || 'OTP generation failed'); }
    finally { setOtpBusy(false); }
  };
  const submitOtp = async () => {
    if (!buyerOtp || buyerOtp.length < 4) { toast.error('OTP daalo (6 digit)'); return; }
    setOtpBusy(true);
    try {
      await api.post(`/atc/sale-post/${post.id}/complete`, { otp: buyerOtp.trim() });
      toast.success('🎉 Deal completed! Seller ko payment ho gaya.');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'OTP verify failed'); }
    finally { setOtpBusy(false); }
  };

  const media = normalizeItems([...(freshPost.images || []).map((u) => ({ url: u, type: 'image' })),
                                ...(freshPost.video_url ? [{ url: freshPost.video_url, type: 'video' }] : [])]);

  // Deal stepper stages
  const stages = [
    { key: 'OPEN',      label: '📝 Open',    active: ['OPEN','LOCKED','COMPLETED'].includes(freshPost.status) },
    { key: 'LOCKED',    label: '🔒 Locked',   active: ['LOCKED','COMPLETED'].includes(freshPost.status) },
    { key: 'OTP',       label: '🔐 OTP',      active: (freshPost.status === 'LOCKED' && (otp || freshPost.completion_otp)) || freshPost.status === 'COMPLETED' },
    { key: 'COMPLETED', label: '✅ Complete', active: freshPost.status === 'COMPLETED' },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="atc-sale-detail-modal" onClick={onClose}>
      <div className="max-w-2xl w-full rounded-2xl bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 border-2 border-emerald-400/40 p-4 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <div className="text-lg font-black">{freshPost.title}</div>
            <div className="text-[11px] opacity-75">
              🏷️ {freshPost.category} · 📍 {freshPost.city || 'Any city'} · @{freshPost.seller_username}
            </div>
          </div>
          <button onClick={onClose} data-testid="atc-sale-detail-close" className="text-slate-300 text-2xl px-1">×</button>
        </div>

        {/* Deal stepper */}
        <div className="flex items-center justify-between mb-3 rounded-xl bg-slate-950/70 border border-slate-700 p-2" data-testid="atc-sale-stepper">
          {stages.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`text-[10px] font-black text-center transition ${s.active ? 'text-emerald-300' : 'text-slate-500'}`}>
                {s.label}
              </div>
              {i < stages.length - 1 && (
                <div className={`flex-1 mx-1 h-0.5 ${stages[i+1].active ? 'bg-emerald-400' : 'bg-slate-700'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {media.length > 0 && (
          <div className="flex gap-1 overflow-x-auto mb-2 pb-1">
            {media.map((m, i) => (
              <div key={i} onClick={() => setLightbox({ items: media, index: i })}
                   className="w-24 h-24 rounded overflow-hidden border border-slate-700 cursor-pointer flex-shrink-0 relative">
                {m.type === 'video'
                  ? <video src={m.url} className="w-full h-full object-cover" muted />
                  : <img src={m.url} alt="" className="w-full h-full object-cover" />}
                {m.type === 'video' && <div className="absolute inset-0 flex items-center justify-center text-white text-2xl">▶</div>}
              </div>
            ))}
          </div>
        )}
        <div className="text-xs whitespace-pre-wrap opacity-90 mb-2">{freshPost.description}</div>

        <div className="rounded-xl bg-slate-950/50 border border-emerald-400/30 p-3 mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] opacity-75">
              {freshPost.status === 'LOCKED' ? '🔒 Locked at' : freshPost.status === 'COMPLETED' ? '✅ Sold at' : 'Seller expects'}
            </span>
            {freshPost.status === 'OPEN' && <CountdownTimer expiresAt={freshPost.expires_at} />}
          </div>
          <div className="text-2xl font-black text-emerald-300">
            ₹{freshPost.locked_price_at || freshPost.price_expectation_at} <span className="text-xs opacity-70">AT</span>
          </div>
        </div>

        {/* Wave 42-fix · Seller OTP panel */}
        {isMine && freshPost.status === 'LOCKED' && (
          <div className="rounded-xl bg-gradient-to-br from-amber-950/60 to-slate-950 border-2 border-amber-400/50 p-3 mb-3"
               data-testid="atc-sale-seller-otp-panel">
            <div className="text-xs font-black text-amber-200 mb-1">🔐 Deal Locked with @{freshPost.buyer_username}</div>
            <div className="text-[11px] opacity-80 mb-2">
              Item handover ke time, ye 6-digit OTP buyer ko share karo. Buyer confirm karega → paisa aapko release.
            </div>
            {otp || freshPost.completion_otp ? (
              <div className="text-center">
                <div className="inline-block px-6 py-3 rounded-xl bg-slate-950 border-2 border-amber-400 shadow-lg shadow-amber-500/30">
                  <div className="text-[9px] font-black uppercase tracking-widest opacity-70 mb-1">OTP for Buyer</div>
                  <div className="text-4xl font-black font-mono tracking-widest text-amber-300"
                       data-testid="atc-sale-otp-display">
                    {otp || freshPost.completion_otp}
                  </div>
                </div>
                <div className="mt-2 flex gap-2 justify-center">
                  <button onClick={() => { navigator.clipboard.writeText(otp || freshPost.completion_otp); toast.success('OTP copied'); }}
                          className="px-3 py-1 rounded-lg bg-slate-700 text-xs font-black">🔗 Copy</button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`🔐 ATC Deal OTP: ${otp || freshPost.completion_otp} — enter this on the app after receiving the item.`)}`}
                     target="_blank" rel="noreferrer"
                     className="px-3 py-1 rounded-lg bg-green-500 text-white text-xs font-black">💬 WhatsApp</a>
                </div>
              </div>
            ) : (
              <button onClick={generateOtp} disabled={otpBusy} data-testid="atc-sale-generate-otp"
                      className="w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 text-sm font-black hover:brightness-110 disabled:opacity-50">
                {otpBusy ? '⏳' : '🔐 Generate OTP · Share with Buyer'}
              </button>
            )}
          </div>
        )}

        {/* Wave 42-fix · Buyer complete panel */}
        {isLockedBuyer && (
          <div className="rounded-xl bg-gradient-to-br from-cyan-950/60 to-slate-950 border-2 border-cyan-400/60 p-3 mb-3"
               data-testid="atc-sale-buyer-complete-panel">
            <div className="text-xs font-black text-cyan-200 mb-1">✅ Confirm Received · Enter OTP</div>
            <div className="text-[11px] opacity-80 mb-2">
              Item mila? Seller se OTP le lo (WhatsApp/in-person). Yahaan enter karke deal complete karo → paisa release ho jayega.
            </div>
            <div className="flex gap-2">
              <input value={buyerOtp} onChange={(e) => setBuyerOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                     placeholder="6-digit OTP" maxLength={6} inputMode="numeric"
                     data-testid="atc-sale-buyer-otp-input"
                     className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border-2 border-cyan-400/40 text-center text-2xl font-mono font-black tracking-widest" />
              <button onClick={submitOtp} disabled={otpBusy || buyerOtp.length < 4}
                      data-testid="atc-sale-buyer-submit-otp"
                      className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 text-sm font-black hover:brightness-110 disabled:opacity-50">
                {otpBusy ? '⏳' : '✅ Complete'}
              </button>
            </div>
          </div>
        )}

        {/* Completed state */}
        {freshPost.status === 'COMPLETED' && (
          <div className="rounded-xl bg-emerald-500/20 border-2 border-emerald-400/60 p-3 mb-3 text-center"
               data-testid="atc-sale-completed-panel">
            <div className="text-3xl mb-1">🎉</div>
            <div className="text-sm font-black text-emerald-200">Deal Completed!</div>
            <div className="text-[11px] opacity-80">Seller ko ₹{freshPost.locked_price_at} AT payout ho gaya · escrow released</div>
          </div>
        )}

        {/* Wave 44 · Rate + Dispute panels (buyer-only, COMPLETED, within 24h) */}
        {freshPost.status === 'COMPLETED' && currentUser?.id === freshPost.buyer_id && (
          <PostDealActionsSale post={freshPost} onChanged={load} />
        )}

        {!isMine && freshPost.status === 'OPEN' && (
          <div className="rounded-xl bg-slate-950/60 border border-cyan-400/30 p-3 mb-3">
            <div className="text-xs font-black mb-1">💬 Aapka Offer</div>
            <div className="flex gap-2">
              <input type="number" value={offerPrice} onChange={(e) => setOfferPrice(e.target.value)}
                     placeholder="Your price (AT)" data-testid="atc-sale-offer-input"
                     className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm" />
              <button onClick={placeOffer} disabled={busy} data-testid="atc-sale-offer-submit"
                      className="px-4 py-2 rounded-lg bg-cyan-500 text-slate-900 text-sm font-black hover:brightness-110 disabled:opacity-50">
                {busy ? '…' : 'Send Offer'}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-1.5" data-testid="atc-sale-offers-list">
          <div className="text-xs font-black mb-1">📨 Offers ({offers.length}) {sellerView && <span className="opacity-60">· seller view</span>}</div>
          {offers.length === 0 && <div className="text-[11px] opacity-60 text-center py-3">Abhi koi offer nahi</div>}
          {offers.map((o) => {
            const shown = o.counter_price_at || o.price_at;
            const statusColor = o.status === 'ACCEPTED' ? 'text-emerald-300' :
                                o.status === 'COUNTERED' ? 'text-amber-300' :
                                o.status === 'REJECTED' ? 'text-rose-300' : 'text-cyan-300';
            return (
              <div key={o.id} data-testid={`atc-sale-offer-${o.id.slice(0,8)}`}
                   className="rounded-lg bg-slate-800/50 border border-slate-700 p-2 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[11px] flex-1">
                  <div className="font-black flex items-center gap-1 flex-wrap">
                    <span>@{o.buyer_username}</span>
                    {o.buyer_priority && (
                      <span data-testid={`atc-priority-buyer-${o.id.slice(0,8)}`}
                            className="px-1 py-0.5 rounded bg-amber-500/30 text-amber-200 text-[9px] font-black animate-pulse">
                        ⚡ Priority Buyer · −5%
                      </span>
                    )}
                    <span> → <span className="text-emerald-300">₹{shown} AT</span> {o.counter_price_at && <span className="opacity-60 line-through">₹{o.price_at}</span>}</span>
                  </div>
                  <div className={`text-[10px] font-black ${statusColor}`}>{o.status}</div>
                </div>
                {sellerView && (o.status === 'PENDING' || o.status === 'COUNTERED') && (
                  <div className="flex gap-1">
                    <button onClick={() => accept(o)} data-testid={`atc-sale-accept-${o.id.slice(0,8)}`}
                            className="px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">✓ Accept</button>
                    {o.status === 'PENDING' && (
                      <button onClick={() => counter(o)} data-testid={`atc-sale-counter-${o.id.slice(0,8)}`}
                              className="px-2 py-1 rounded bg-amber-500 text-slate-900 text-[10px] font-black">↔ Counter</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {lightbox && <MediaLightbox items={lightbox.items} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
      </div>
    </div>
  );
}


// ================= Wave 39 · Deal of the Hour Banner =================
function DealOfTheHourBanner({ dealType, onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/atc/sale-posts/deal-of-hour', { params: { deal_type: dealType } });
      setData(r.data);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, [dealType]);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  if (loading) return null;
  if (!data?.post) return null;
  const p = data.post;

  return (
    <div onClick={() => onOpen(p)} data-testid="atc-deal-of-hour-banner"
         className="cursor-pointer mb-4 rounded-2xl p-3 bg-gradient-to-r from-orange-900 via-rose-900 to-fuchsia-900 border-2 border-orange-400/60 shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition atc-doth-pulse">
      <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
        {p.images?.[0] && (
          <img src={p.images[0]} alt="" className="w-24 h-24 rounded-lg object-cover border-2 border-orange-400/70 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-orange-300 animate-pulse">🔥 Deal of the Hour</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-950/60 border border-orange-400/40 font-black text-orange-200">
              Score {data.score}
            </span>
          </div>
          <div className="text-base sm:text-lg font-black text-white line-clamp-1">{p.title}</div>
          <div className="flex items-center gap-2 flex-wrap text-[11px] mt-1 opacity-90">
            <span className="text-emerald-300 font-black">₹{p.price_expectation_at} AT</span>
            <span>🏷️ {p.category}</span>
            {p.city && <span>📍 {p.city}</span>}
            <span className="text-orange-200 font-black animate-pulse">
              🔥 {data.offers_last_hour} offer{data.offers_last_hour !== 1 ? 's' : ''} in last hour
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <CountdownTimer expiresAt={p.expires_at} />
          <button data-testid="atc-doth-cta"
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-orange-400 to-rose-400 text-slate-900 text-xs font-black hover:brightness-110">
            Place Offer Now →
          </button>
        </div>
      </div>
    </div>
  );
}


// ================= Wave 39 · Sale Analytics Block =================
function SaleAnalyticsBlock({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    api.get(`/atc/seller/${userId}/sale-analytics`, { params: { days: 30 } })
      .then((r) => { if (!cancel) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [userId]);

  if (loading) return <div className="mb-4 text-xs opacity-60 text-center py-4">⏳ Loading analytics…</div>;
  if (!data) return null;
  const t = data.totals || {};

  return (
    <div className="mb-4 rounded-xl bg-slate-900/70 border-2 border-fuchsia-400/30 p-3" data-testid="atc-sale-analytics">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-sm font-black text-fuchsia-300">📊 My Sales Stats · last {data.days}d</div>
          <div className="text-[10px] opacity-70">
            {data.top_category && <>Top-selling category: <b className="text-emerald-300">{data.top_category}</b></>}
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10px] opacity-80">
          <span>7d views</span>
          <Sparkline data={data.views_sparkline_7d || []} width={80} height={20} color="#e879f9" showArea />
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
        <StatMini label="Posts" value={t.posts} color="cyan" />
        <StatMini label="Views" value={t.views} color="fuchsia" />
        <StatMini label="Offers" value={t.offers} color="amber" />
        <StatMini label="Locked" value={t.locked} color="emerald" />
        <StatMini label="Completed" value={t.completed} color="emerald" />
        <StatMini label="Conv %" value={`${t.conversion_pct || 0}%`} color="rose" />
      </div>
      {data.top_by_conversion?.length > 0 && (
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">🏆 Top by conversion</div>
          <div className="space-y-1">
            {data.top_by_conversion.map((p, i) => (
              <div key={p.id} data-testid={`atc-analytics-top-${i+1}`}
                   className="flex items-center justify-between gap-2 text-[11px] rounded bg-slate-800/60 px-2 py-1">
                <span className="truncate">
                  <span className="opacity-60 mr-1">{['🥇','🥈','🥉'][i]}</span>{p.title}
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className="opacity-70">👁 {p.views}</span>
                  <span className="opacity-70">💬 {p.offers}</span>
                  <span className="text-emerald-300 font-black">{p.conversion_pct}%</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value, color }) {
  const colors = {
    cyan: 'text-cyan-300 border-cyan-400/30',
    fuchsia: 'text-fuchsia-300 border-fuchsia-400/30',
    amber: 'text-amber-300 border-amber-400/30',
    emerald: 'text-emerald-300 border-emerald-400/30',
    rose: 'text-rose-300 border-rose-400/30',
  }[color] || 'text-slate-300 border-slate-500/30';
  return (
    <div className={`rounded-lg bg-slate-950/60 border p-2 text-center ${colors}`}>
      <div className="text-lg font-black tabular-nums">{value}</div>
      <div className="text-[9px] opacity-70 uppercase tracking-widest">{label}</div>
    </div>
  );
}



// ================= Wave 40 · Weekly Sellers Leaderboard =================
function SellersLeaderboardBanner() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get('/atc/leaderboard/sellers', { params: { window: '7d', limit: 5 } })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);
  if (!data || !data.top_sellers?.length) return null;
  const crowns = ['👑', '🥈', '🥉', '🏅', '🏅'];
  return (
    <div className="mb-4 rounded-2xl p-3 bg-gradient-to-r from-amber-900 via-yellow-900 to-slate-900 border-2 border-amber-300/60 shadow-lg shadow-amber-500/20"
         data-testid="atc-sellers-leaderboard">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-lg animate-pulse">🏆</span>
          <div>
            <div className="text-sm font-black text-amber-200">This Week's Top Sellers</div>
            <div className="text-[10px] opacity-80">Weekly revenue leaderboard · resets Sunday 23:59 UTC</div>
          </div>
        </div>
        <CountdownTimer expiresAt={data.resets_at} compact />
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {data.top_sellers.map((s, i) => (
          <div key={s.seller_id} data-testid={`atc-lb-rank-${s.rank}`}
               className={`flex-shrink-0 rounded-xl px-3 py-2 min-w-[150px] border-2 ${
                 s.rank === 1 ? 'bg-gradient-to-br from-amber-500/30 to-yellow-600/20 border-amber-300 animate-pulse shadow-lg shadow-amber-500/40' :
                 s.rank === 2 ? 'bg-slate-800/60 border-slate-300/50' :
                 s.rank === 3 ? 'bg-orange-900/40 border-orange-400/50' :
                 'bg-slate-900/60 border-slate-700'
               }`}>
            <div className="text-2xl leading-none">{crowns[i]}</div>
            <div className="text-[11px] font-black truncate">@{s.seller_username || 'seller'}</div>
            <div className="text-emerald-300 font-black text-sm">₹{Math.round(s.revenue_at)} <span className="text-[9px] opacity-70">AT</span></div>
            <div className="text-[9px] opacity-70">{s.sales_count} sale{s.sales_count !== 1 ? 's' : ''} · {s.top_category || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ================= Wave 40 · Alerts Bell (deal alerts + price drops) =================
function AlertsBell() {
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/atc/my-alerts');
      setAlerts(r.data.alerts || []);
      setUnread(r.data.unread || 0);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);
  const markRead = async () => {
    try { await api.post('/atc/my-alerts/mark-read'); setUnread(0); } catch (_) {}
  };
  const toggleOpen = () => {
    const nowOpen = !open; setOpen(nowOpen);
    if (nowOpen && unread > 0) markRead();
  };
  return (
    <div className="relative" data-testid="atc-alerts-bell-wrap">
      <button onClick={toggleOpen} data-testid="atc-alerts-bell"
              className="relative px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-cyan-400/40 text-cyan-200 text-xs font-black">
        🔔 Alerts
        {unread > 0 && (
          <span data-testid="atc-alerts-unread-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-black flex items-center justify-center animate-pulse">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 max-h-96 overflow-y-auto rounded-xl bg-slate-950 border-2 border-cyan-400/50 shadow-2xl p-2 z-50"
             data-testid="atc-alerts-dropdown">
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-300 mb-1">Latest Deal Alerts</div>
          {alerts.length === 0 && (
            <div className="text-[11px] opacity-60 text-center py-4">
              No alerts yet.<br/>Follow a category via 🔕 button on any card.
            </div>
          )}
          {alerts.map((a) => (
            <div key={a.id} data-testid={`atc-alert-${a.id.slice(0,8)}`}
                 className={`rounded-lg p-2 mb-1 border ${
                   a.kind === 'price_drop' ? 'bg-rose-950/40 border-rose-400/40' :
                   a.kind === 'otp_ready' ? 'bg-emerald-950/40 border-emerald-400/50 animate-pulse' :
                   'bg-slate-900 border-cyan-400/25'
                 }`}>
              <div className="flex items-center gap-1 text-[10px] font-black">
                {a.kind === 'price_drop' ? '💸 Price Drop' :
                 a.kind === 'otp_ready' ? '🔐 OTP Ready · Click to Enter' :
                 '🔥 New Deal'} · <span className="opacity-70">{a.category || 'ATC'}</span>
              </div>
              <div className="text-xs font-black truncate">{a.title}</div>
              <div className="text-[10px] opacity-80">
                ₹{Math.round(a.price_at)} AT
                {a.kind === 'price_drop' && a.drop_amount ? <span className="text-emerald-300"> · save ₹{Math.round(a.drop_amount)}</span> : null}
              </div>
              <div className="mt-1 flex gap-1">
                {a.kind !== 'otp_ready' && (
                  <a href={a.wa_link} target="_blank" rel="noreferrer" data-testid={`atc-alert-wa-${a.id.slice(0,8)}`}
                     className="flex-1 text-center px-2 py-0.5 rounded bg-green-500/80 text-white text-[10px] font-black hover:brightness-110">
                    💬 Share on WhatsApp
                  </a>
                )}
                <a href={a.kind === 'otp_ready' ? '/atc/b2c' : a.share_url} target={a.kind === 'otp_ready' ? '_self' : '_blank'} rel="noreferrer"
                   className={`px-2 py-0.5 rounded text-[10px] font-black hover:brightness-110 ${
                     a.kind === 'otp_ready' ? 'flex-1 text-center bg-emerald-500 text-slate-900' : 'bg-cyan-500/60 text-slate-900'
                   }`}>
                  {a.kind === 'otp_ready' ? '🔐 Enter OTP →' : '→ View'}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ================= Wave 40 · Wishlist Drawer =================
function WishlistDrawer({ onClose, onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/atc/wishlist').then((r) => { setItems(r.data.items || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="atc-wishlist-drawer" onClick={onClose}>
      <div className="max-w-lg w-full rounded-2xl bg-gradient-to-br from-slate-900 via-rose-950 to-slate-900 border-2 border-rose-400/50 p-4 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-lg font-black text-rose-200">💖 My Wishlist</div>
            <div className="text-[10px] opacity-75">Saved items with live price-drop tracking</div>
          </div>
          <button onClick={onClose} data-testid="atc-wishlist-close" className="text-slate-300 text-xl px-1">×</button>
        </div>
        {loading ? (
          <div className="text-center py-6 text-xs opacity-60">⏳ Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-xs opacity-60 rounded-xl border border-dashed border-rose-400/30">
            Aapki wishlist khaali hai. Kisi bhi sale-post pe 🤍 dabao aur save karo!
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.post.id} onClick={() => onOpen(it.post)} data-testid={`atc-wish-item-${it.post.id.slice(0,8)}`}
                   className="cursor-pointer rounded-xl p-2 bg-slate-900/70 border border-rose-400/25 hover:border-rose-400/60 flex items-center gap-2">
                {it.post.images?.[0]
                  ? <img src={it.post.images[0]} alt="" className="w-16 h-16 rounded object-cover" />
                  : <div className="w-16 h-16 rounded bg-slate-800 flex items-center justify-center text-2xl">🛍️</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black truncate">{it.post.title}</div>
                  <div className="text-[10px] opacity-70">🏷️ {it.post.category} · {it.post.status}</div>
                  <div className="text-[11px] mt-0.5">
                    <span className="text-emerald-300 font-black">₹{Math.round(it.price_now)} AT</span>
                    {it.price_dropped && (
                      <>
                        <span className="opacity-60 line-through ml-1">₹{Math.round(it.price_at_save)}</span>
                        <span data-testid={`atc-wish-drop-${it.post.id.slice(0,8)}`}
                              className="ml-1 px-1 py-0.5 rounded bg-emerald-500 text-slate-900 text-[9px] font-black animate-pulse">
                          💸 −₹{Math.round(it.drop_amount)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <CountdownTimer expiresAt={it.post.expires_at} compact />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



// ================= Wave 41 · Launch Party thin strip (top of Sellers Bazaar) =================
function LaunchPartyStrip() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get('/atc/launch-party').then((r) => { if (!cancel) setStatus(r.data); }).catch(() => {});
    return () => { cancel = true; };
  }, []);
  if (!status?.is_live) return null;
  return (
    <a href="/atc/launch-party" data-testid="atc-launch-strip"
       className="block mb-3 rounded-xl px-3 py-2 bg-gradient-to-r from-fuchsia-600 via-rose-500 to-amber-500 hover:brightness-110 shadow-lg shadow-fuchsia-500/30 transition">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xl animate-pulse">🎉</span>
          <div className="text-white">
            <div className="text-sm font-black leading-tight">LAUNCH PARTY LIVE · Grab 10 AT bonus</div>
            <div className="text-[10px] opacity-90">
              {status.slots_taken} / {status.slots_total} slots claimed · only {status.slots_remaining} left!
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CountdownTimer expiresAt={status.live_until} compact />
          <span className="px-3 py-1 rounded-lg bg-white/20 text-white text-xs font-black">
            Claim Slot →
          </span>
        </div>
      </div>
    </a>
  );
}


// ================= Wave 41 · Trending Category Cloud =================
function TrendingCategoryCloud({ onPick }) {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    let cancel = false;
    api.get('/atc/trending-categories', { params: { days: 7, limit: 15 } })
      .then((r) => { if (!cancel) setCats(r.data.categories || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);
  if (!cats.length) return null;
  const maxScore = Math.max(...cats.map((c) => c.score), 1);
  return (
    <div className="mb-4 rounded-xl bg-slate-950/60 border border-cyan-400/30 p-3" data-testid="atc-trending-cloud">
      <div className="text-[10px] font-black uppercase tracking-widest text-cyan-300 mb-2">
        ☁️ Trending in the last 7 days
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 justify-center">
        {cats.map((c, i) => {
          const rel = c.score / maxScore;
          // font size from 12px to 30px based on relative score
          const size = Math.round(12 + rel * 18);
          const colors = ['text-cyan-300', 'text-emerald-300', 'text-amber-300', 'text-fuchsia-300', 'text-rose-300'];
          const color = colors[i % colors.length];
          return (
            <button key={c.name} onClick={() => onPick?.(c.name)}
                    data-testid={`atc-cloud-chip-${c.name.toLowerCase().replace(/\s|&/g, '-')}`}
                    className={`atc-cloud-chip font-black hover:underline ${color} transition`}
                    style={{ fontSize: `${size}px`, animationDelay: `${i * 0.15}s` }}
                    title={`${c.follow_count} followers · ${c.post_count} posts`}>
              #{c.name.replace(/\s+&\s+/g, '-').replace(/\s+/g, '')}
            </button>
          );
        })}
      </div>
    </div>
  );
}



// ================= Wave 44 · Post-Deal Rate + Dispute (buyer, COMPLETED, 24h) =================
function PostDealActionsSale({ post, onChanged }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [rated, setRated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [dispReason, setDispReason] = useState('not_delivered');
  const [dispDesc, setDispDesc] = useState('');
  const [disputed, setDisputed] = useState(false);

  // Check 24h window
  const withinWindow = React.useMemo(() => {
    if (!post.completed_at) return false;
    try {
      const dt = new Date(post.completed_at).getTime();
      return (Date.now() - dt) < 24 * 3600 * 1000;
    } catch (_) { return false; }
  }, [post.completed_at]);

  const submitRating = async () => {
    if (!stars) { toast.error('Star chuno pehle'); return; }
    setBusy(true);
    try {
      await api.post(`/atc/sale-post/${post.id}/rate`, { stars, comment: comment.trim() || undefined });
      toast.success(`⭐ Thanks — ${stars}-star rating recorded`);
      setRated(true); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rate failed'); }
    finally { setBusy(false); }
  };
  const submitDispute = async () => {
    if (!dispDesc.trim() || dispDesc.trim().length < 5) { toast.error('Describe the issue (min 5 chars)'); return; }
    setBusy(true);
    try {
      await api.post(`/atc/sale-post/${post.id}/dispute`, { reason: dispReason, description: dispDesc.trim() });
      toast.success('🚨 Dispute raised — admin will review within 24h');
      setDisputed(true); setShowDispute(false); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Dispute failed'); }
    finally { setBusy(false); }
  };

  if (!withinWindow) return null;
  return (
    <div className="rounded-xl bg-slate-950/70 border border-emerald-400/25 p-3 mb-3" data-testid="atc-sale-post-actions">
      {!rated && !disputed && (
        <>
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-2">
            ⭐ Rate this seller · 24h window
          </div>
          <div className="flex items-center gap-1 mb-2" data-testid="atc-rate-stars">
            {[1,2,3,4,5].map((n) => (
              <button key={n} onClick={() => setStars(n)}
                      data-testid={`atc-rate-star-${n}`}
                      className={`text-2xl transition ${n <= stars ? 'text-amber-300' : 'text-slate-600 hover:text-amber-500'}`}>★</button>
            ))}
            {stars > 0 && <span className="ml-2 text-xs opacity-80">{stars}/5</span>}
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                    placeholder="Comment (optional)…"
                    data-testid="atc-rate-comment"
                    className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs mb-2" />
          <div className="flex gap-2">
            <button onClick={submitRating} disabled={busy || !stars} data-testid="atc-rate-submit"
                    className="flex-1 px-3 py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black hover:brightness-110 disabled:opacity-50">
              {busy ? '⏳' : '✅ Submit Rating'}
            </button>
            <button onClick={() => setShowDispute(true)} data-testid="atc-dispute-open"
                    className="px-3 py-2 rounded-lg bg-rose-500/30 text-rose-200 text-xs font-black hover:bg-rose-500/50">
              🚨 Something wrong?
            </button>
          </div>
        </>
      )}
      {rated && !disputed && (
        <div className="text-xs opacity-80 text-center py-2" data-testid="atc-rate-done">
          ⭐ Rating submitted · shukriya!
        </div>
      )}
      {disputed && (
        <div className="text-xs text-rose-200 text-center py-2" data-testid="atc-dispute-done">
          🚨 Dispute raised · admin will investigate within 24h
        </div>
      )}
      {showDispute && !disputed && (
        <div className="mt-2 rounded-lg bg-rose-950/50 border border-rose-400/40 p-2" data-testid="atc-dispute-form">
          <div className="text-[10px] font-black uppercase text-rose-200 mb-1">🚨 Raise Dispute</div>
          <select value={dispReason} onChange={(e) => setDispReason(e.target.value)}
                  data-testid="atc-dispute-reason"
                  className="w-full px-2 py-1 rounded bg-slate-900 border border-slate-700 text-xs mb-1">
            <option value="not_delivered">Item not delivered</option>
            <option value="damaged">Item damaged / broken</option>
            <option value="fake">Fake / wrong item</option>
            <option value="other">Other</option>
          </select>
          <textarea value={dispDesc} onChange={(e) => setDispDesc(e.target.value)} rows={2}
                    placeholder="Describe issue (min 5 chars)…"
                    data-testid="atc-dispute-desc"
                    className="w-full px-2 py-1 rounded bg-slate-900 border border-slate-700 text-xs mb-1" />
          <div className="flex gap-2">
            <button onClick={submitDispute} disabled={busy} data-testid="atc-dispute-submit"
                    className="flex-1 px-3 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-black hover:brightness-110 disabled:opacity-50">
              {busy ? '⏳' : '🚨 Raise Dispute'}
            </button>
            <button onClick={() => setShowDispute(false)}
                    className="px-3 py-1.5 rounded-lg bg-slate-700 text-xs font-black">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

